import { useReducer } from 'react';
import createUseContext from 'constate';
import { getUrgentSku, getStandardSku, getSystemType } from '../utils';
import queryString from 'query-string';

const orderData = document.documentElement.dataset.orderData;
const issueState = orderData ? JSON.parse(orderData) : {};
const severeIssueType = 'severe';
const issueType = queryString.parse(document.location.search)['issue_type'] || '';
const deliveryType = queryString.parse(document.location.search)['delivery_type'] || '';
const systemType = getSystemType(queryString.parse(document.location.search)['system_type']);
const isCoschedulingEnabled = document.documentElement.dataset.isCoschedulingEnabled === 'true';

const initialState = {
  service: {
    date: '',
    product_sku: (issueType === severeIssueType && !isCoschedulingEnabled) ? getUrgentSku(systemType) : getStandardSku(systemType),
    time: '',
    third_party_allowed: true,
    auto_selected_date: true,
  },
  user: {
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    stripe_token: ''
  },
  address: {
    address1: '',
    city: '',
    zipcode: queryString.parse(document.location.search)['postal_code'] || '',
    state: ''
  },
  quote: {},
  order: {},
  isLoading: false,
  errorMessage: null,
  onBehalfOfPartnerMachineName: null,
  issueType: issueType,
  delivery_type: deliveryType,
  systemType: systemType,
  ...issueState,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "UPDATE_SERVICE":
      return {
        ...state,
        service: {...state.service, ...action.payload}
      };
    case "UPDATE_USER":
      return {
        ...state,
        user: {...state.user, ...action.payload}
      };
    case "UPDATE_ADDRESS":
      return {
        ...state,
        address: {...state.address, ...action.payload}
      };
    case "UPDATE_ON_BEHALF_OF_PARTNER_MACHINE_NAME":
      return {
        ...state,
        onBehalfOfPartnerMachineName: action.payload
      };
    case "UPDATE_QUOTE":
      return {
        ...state,
        quote: action.payload
      };
    case "UPDATE_ORDER":
      return {
        ...state,
        order: {...state.order, ...action.payload}
      };
    case "REMOVE_SERVICE":
      return {
        ...state,
        service: {}
      };
    case "REMOVE_USER":
      return {
        ...state,
        user: {}
      };
    case "REMOVE_ADDRESS":
      return {
        ...state,
        address: {}
      };
    case "REMOVE_QUOTE":
      return {
        ...state,
        quote: {}
      };
    case "START":
      return {
        ...state,
        isLoading: true
      };
    case "COMPLETE":
      return {
        ...state,
        isLoading: false
      };
    case "SHOW_ERROR":
      return {
        ...state,
        errorMessage: action.payload
      };
    case "HIDE_ERROR":
      return {
        ...state,
        errorMessage: null
      };
    default:
      throw new Error();
  }
};

const useForm = () => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const {
    service,
    user,
    address,
    quote,
    order,
    isLoading,
    errorMessage,
    onBehalfOfPartnerMachineName,
    systemType,
  } = state;

  const updateFormInput = data => {
    const { service, user, address, onBehalfOfPartnerMachineName} = data;

    if (service != null) {
      dispatch({
        type: "UPDATE_SERVICE",
        payload: service,
      })
    }

    if (user != null) {
      dispatch({
        type: "UPDATE_USER",
        payload: user,
      })
    }

    if (address != null) {
      dispatch({
        type: "UPDATE_ADDRESS",
        payload: address,
      })
    }

    if (onBehalfOfPartnerMachineName != null) {
      dispatch({
        type: "UPDATE_ON_BEHALF_OF_PARTNER_MACHINE_NAME",
        payload: onBehalfOfPartnerMachineName,
      })
    }
  }

  const updateQuote = quote => {
    dispatch({
      type: "UPDATE_QUOTE",
      payload: quote,
    })
  }

  const updateOrder = data => {
    dispatch({
      type: 'UPDATE_ORDER',
      payload: data
    })
  }

  const removeFormInput = () => {
    dispatch({
      type: "REMOVE_SERVICE",
      payload: user,
    })

    dispatch({
      type: "REMOVE_USER",
      payload: {},
    })

    dispatch({
      type: "REMOVE_ADDRESS",
      payload: user,
    })
  }

  const removeQuote = () => {
    dispatch({
      type: "REMOVE_QUOTE",
      payload: {},
    })
  }

  const startLoading = () => {
    dispatch({
      type: "START",
      payload: null,
    })
  }

  const stopLoading = () => {
    dispatch({
      type: "COMPLETE",
      payload: null,
    })
  }

  const showError = err => {
    dispatch({
      type: "SHOW_ERROR",
      payload: err,
    })
  }

  const hideError = () => {
    dispatch({
      type: "HIDE_ERROR",
      payload: null,
    })
  }

  return {
    service,
    user,
    address,
    quote,
    order,
    isLoading,
    errorMessage,
    onBehalfOfPartnerMachineName,
    systemType,
    startLoading,
    stopLoading,
    updateFormInput,
    updateQuote,
    updateOrder,
    removeQuote,
    removeFormInput,
    showError,
    hideError
  };
};

export const useFormContext = createUseContext(useForm);
